import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi } from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  isAdmin: boolean;
  isAuthenticated: boolean;
  user: User | null;
  email: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * JWT 토큰에서 만료 시간 추출
 */
function getTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) payload += '=';
    const decoded = JSON.parse(atob(payload));
    return decoded.exp || null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 초기화: localStorage에서 토큰/사용자 복원
  useEffect(() => {
    const token = localStorage.getItem('u3p_token');
    const savedUser = localStorage.getItem('u3p_user');

    if (token && savedUser) {
      // 토큰 만료 확인
      const exp = getTokenExpiry(token);
      const now = Math.floor(Date.now() / 1000);

      if (exp && exp > now) {
        try {
          setUser(JSON.parse(savedUser));
        } catch {
          clearAuth();
        }
      } else {
        // 만료된 토큰 — 갱신 시도
        authApi.refresh()
          .then((res) => {
            const { token: newToken, user: userData } = res.data.data;
            localStorage.setItem('u3p_token', newToken);
            localStorage.setItem('u3p_user', JSON.stringify(userData));
            setUser(userData);
          })
          .catch(() => {
            clearAuth();
          });
      }
    }
    setLoading(false);
  }, []);

  // 토큰 자동 갱신 (만료 5분 전)
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('u3p_token');
    if (!token) return;

    const exp = getTokenExpiry(token);
    if (!exp) return;

    const now = Math.floor(Date.now() / 1000);
    const refreshIn = Math.max((exp - now - 300) * 1000, 0); // 만료 5분 전

    const timer = setTimeout(async () => {
      try {
        const res = await authApi.refresh();
        const { token: newToken, user: userData } = res.data.data;
        localStorage.setItem('u3p_token', newToken);
        localStorage.setItem('u3p_user', JSON.stringify(userData));
        setUser(userData);
      } catch {
        clearAuth();
      }
    }, refreshIn);

    return () => clearTimeout(timer);
  }, [user]);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('u3p_token');
    localStorage.removeItem('u3p_user');
    localStorage.removeItem('admin');
    localStorage.removeItem('email');
    setUser(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await authApi.login(email, password);
      const { token, user: userData } = res.data.data;

      localStorage.setItem('u3p_token', token);
      localStorage.setItem('u3p_user', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (err: any) {
      const message = err.response?.data?.message || '로그인에 실패했습니다.';
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const email = user?.email || null;

  return (
    <AuthContext.Provider value={{ isAdmin, isAuthenticated, user, email, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
